import React from "react";

import { useTranslation } from "react-i18next";

import { ButtonGroup } from "@caelor/ui";
import ColorPicker from "../../../../../../components/ui/color-picker/ColorPicker";
import {
  OptionContainer,
  OptionContent,
  OptionLabel,
  SettingsOptionContainer,
  SettingsOptionHeading,
} from "../../styles";
import { BOOLEAN_OPTIONS, CORNERS_OPTIONS } from "./options";
import {
  ANNOUNCEMENTS_CONFIG_PROP,
  DESIGN_STYLES,
  LAYOUT_OPTIONS_BASED_ON_DESIGN,
} from "./defaults";
import { useAnnouncementsStore } from "../../../../../../stores/use-announcements-store";
import { useTheme } from "styled-components";

function AnnouncementsOptions() {
  const { t } = useTranslation();
  const theme = useTheme();

  const { data, config, updateConfig, updateAnnouncementsColors } =
    useAnnouncementsStore((state) => ({
      data: state.data,
      config: state.config,
      updateConfig: state.updateConfig,
      updateAnnouncementsColors: state.updateAnnouncementsColors,
    }));

  const { designStyle } = config;
  const isOldDesignStyle = !Object.values(DESIGN_STYLES).includes(designStyle);

  const allAnnouncementsColors =
    data?.map((item) => ({
      accent: item.color,
      text: item.textColor,
    })) || [];

  const firstColor = allAnnouncementsColors?.[0] || {
    accent: "#000",
    text: "#fff",
  };

  const [accentColor, textColor] = allAnnouncementsColors.reduce(
    (acc, color) => {
      if (color.accent === firstColor.accent) {
        acc[0] = color.accent;
      }
      if (color.text === firstColor.text) {
        acc[1] = color.text;
      }
      return acc;
    },
    []
  );

  const buildLayout = (isOn, prop) => {
    const layout = isOn
      ? [...config[ANNOUNCEMENTS_CONFIG_PROP.LAYOUT_STRUCTURE], prop]
      : config[ANNOUNCEMENTS_CONFIG_PROP.LAYOUT_STRUCTURE].filter(
          (item) => item !== prop
        );

    updateConfig({
      key: ANNOUNCEMENTS_CONFIG_PROP.LAYOUT_STRUCTURE,
      value: layout,
    });
  };

  return (
    <>
      <SettingsOptionContainer>
        <SettingsOptionHeading>
          {t("flexible-display-items")}
        </SettingsOptionHeading>
        {LAYOUT_OPTIONS_BASED_ON_DESIGN[designStyle]?.map(({ prop, title }) => (
          <OptionContainer key={title}>
            <OptionLabel>{t(title)}</OptionLabel>
            <OptionContent>
              <ButtonGroup
                items={BOOLEAN_OPTIONS}
                value={config[
                  ANNOUNCEMENTS_CONFIG_PROP.LAYOUT_STRUCTURE
                ].includes(prop)}
                onClick={(isOn) => buildLayout(isOn, prop)}
                i18n={t}
              />
            </OptionContent>
          </OptionContainer>
        ))}
      </SettingsOptionContainer>

      <SettingsOptionContainer>
        <SettingsOptionHeading>{t("styling-options")}</SettingsOptionHeading>
        <OptionContainer>
          <OptionLabel>{t("corners")}</OptionLabel>
          <OptionContent>
            <ButtonGroup
              items={CORNERS_OPTIONS}
              value={config[ANNOUNCEMENTS_CONFIG_PROP.BORDER_RADIUS]}
              options={{ input: true }}
              onClick={(value) =>
                updateConfig({
                  key: ANNOUNCEMENTS_CONFIG_PROP.BORDER_RADIUS,
                  value,
                })
              }
              onChange={({ target: { value } }) => {
                if (value.length < 4)
                  updateConfig({
                    key: ANNOUNCEMENTS_CONFIG_PROP.BORDER_RADIUS,
                    value,
                  });
              }}
              i18n={t}
            />
          </OptionContent>
        </OptionContainer>
        <OptionContainer>
          <OptionLabel alignToTop>{t("colors")}</OptionLabel>
          <OptionContent>
            <ColorPicker
              value={{ accent: accentColor || {}, text: textColor || {} }}
              onChange={(color) => updateAnnouncementsColors(color)}
            />
          </OptionContent>
        </OptionContainer>
      </SettingsOptionContainer>
    </>
  );
}

export default AnnouncementsOptions;
